"use client";

/**
 * Undo for the destructive taps (owner, 2026-10-05: "if I remove
 * something from the watchlist, I get a toast, it's ticking, I can
 * click undo"). The UI applies the removal at once; the server call
 * waits for the toast to run out. Undo puts the thing back and the
 * server never hears of it. Leaving the page commits what is pending.
 *
 *   const undo = useUndo();
 *   undo.schedule({ message, commit: () => api.delete(id), undo: () => store.restore(item) });
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RotateCcw, X } from "lucide-react";
import { useT } from "@/lib/locale-context";

export interface UndoRequest {
  /** What was done, as the toast says it. */
  message: string;
  /** How long the person has, ms. */
  ttl?: number;
  /** The real removal, run when the toast runs out or is dismissed. */
  commit: () => void | Promise<void>;
  /** Put the thing back in the UI. */
  undo: () => void | Promise<void>;
  /** The same key again (a tap repeated on the same thing) settles the
   *  earlier toast and shows one, never a stack of duplicates (owner,
   *  2026-10-10: "if I press it nonstop, I get duplicate undos"). */
  key?: string;
}

interface Entry extends Required<Omit<UndoRequest, "key">> { id: number; startedAt: number; key: string | null }

const Ctx = createContext<{ schedule: (r: UndoRequest) => void } | null>(null);
const DEFAULT_TTL = 6000;

// Outside the provider (a test, a stray surface) the removal just happens.
// One shared object, so callers that list `undo` in deps stay stable.
const NO_UNDO = { schedule: (r: UndoRequest) => { void r.commit(); } };

export function useUndo() {
  return useContext(Ctx) ?? NO_UNDO;
}

export function UndoProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const pending = useRef(new Map<number, Entry>());
  const seq = useRef(0);

  const finish = useCallback((id: number, how: "commit" | "undo") => {
    const e = pending.current.get(id);
    if (!e) return;
    pending.current.delete(id);
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setEntries((cur) => cur.filter((x) => x.id !== id));
    void (how === "commit" ? e.commit() : e.undo());
  }, []);

  const schedule = useCallback((r: UndoRequest) => {
    if (r.key) {
      for (const [pid, pe] of [...pending.current.entries()]) if (pe.key === r.key) finish(pid, "commit");
    }
    const id = ++seq.current;
    const e: Entry = { id, startedAt: Date.now(), message: r.message, ttl: r.ttl ?? DEFAULT_TTL, commit: r.commit, undo: r.undo, key: r.key ?? null };
    pending.current.set(id, e);
    setEntries((cur) => [...cur.slice(-2), e]);
    timers.current.set(id, setTimeout(() => finish(id, "commit"), e.ttl));
  }, [finish]);

  // Leaving the page settles everything that is still ticking.
  useEffect(() => {
    const settle = () => { for (const id of [...pending.current.keys()]) finish(id, "commit"); };
    window.addEventListener("pagehide", settle);
    return () => window.removeEventListener("pagehide", settle);
  }, [finish]);

  const value = useMemo(() => ({ schedule }), [schedule]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {typeof document !== "undefined" && entries.length > 0 && createPortal(<Toasts entries={entries} onUndo={(id) => finish(id, "undo")} onClose={(id) => finish(id, "commit")} />, document.body)}
    </Ctx.Provider>
  );
}

function Toasts({ entries, onUndo, onClose }: { entries: Entry[]; onUndo: (id: number) => void; onClose: (id: number) => void }) {
  const t = useT();
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] z-[1250] flex flex-col items-center gap-2 px-4 sm:bottom-6" role="status" aria-live="polite">
      {entries.map((e) => (
        <div key={e.id} className="pointer-events-auto relative w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center gap-3 px-4 py-3">
            <p className="min-w-0 flex-1 text-sm font-medium text-gray-900">{e.message}</p>
            <button type="button" onClick={() => onUndo(e.id)} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700">
              <RotateCcw className="h-4 w-4" aria-hidden />{t("undo.action")}
            </button>
            <button type="button" onClick={() => onClose(e.id)} aria-label={t("undo.dismiss")} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" aria-hidden /></button>
          </div>
          {/* The clock: a bar that empties over the ttl. */}
          <div className="h-1 w-full bg-gray-100" aria-hidden>
            <div className="h-full bg-gray-400" style={{ animation: `undo-tick ${e.ttl}ms linear forwards` }} />
          </div>
        </div>
      ))}
      <style>{`@keyframes undo-tick { from { width: 100%; } to { width: 0%; } }`}</style>
    </div>
  );
}
